import React from 'react'
import * as Sentry from '@sentry/react'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpApi from 'i18next-http-backend'
import { createRoot } from 'react-dom/client'
import { initReactI18next } from 'react-i18next'
import { BrowserRouter as Router } from 'react-router-dom'
import App from './App'
import './index.scss'
import { CacheLocation, FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react'
import { Integrations } from '@sentry/tracing'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import { FINGERPRINT } from 'constants/General'
import { isMswEnabled, isProduction, isSandbox, isTest } from 'utils/environment'
import { onQueryError } from 'utils/reactQuery/onQueryError'
import { onQuerySuccess } from 'utils/reactQuery/onQuerySuccess'

const IS_SENTRY_ENABLED = isProduction || isSandbox
const IS_SENTRY_REPLAY_ENABLED = isProduction
const IS_SENTRY_TRACE_ENABLED = isProduction || isSandbox || isTest
const FINGERPRINT_ENDPOINT = `${process.env.FINGERPRINT_PROXY_HOST}/${process.env.FINGERPRINT_RESULT_PATH}?region=eu`

const FINGERPRINT_SCRIPT_URL_PATTERN = `${process.env.FINGERPRINT_PROXY_HOST}/${process.env.FINGERPRINT_AGENT_DOWNLOAD_PATH}/?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`

Sentry.init({
  normalizeDepth: 10,
  enabled: !!IS_SENTRY_ENABLED,
  environment: process.env.ENV,
  dsn: process.env.SENTRY_DSN,
  ...(!!IS_SENTRY_REPLAY_ENABLED && {
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  }),
  integrations: [
    !!IS_SENTRY_TRACE_ENABLED && new Integrations.BrowserTracing(),
    ...(!!IS_SENTRY_REPLAY_ENABLED
      ? [
          new Sentry.Replay({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false,
          }),
        ]
      : []),
  ],
  tracesSampleRate: !!IS_SENTRY_TRACE_ENABLED && 0.5,
  ignoreErrors: ['NetworkError', 'Network Error', 'LaunchDarklyFlagFetchError'],
  beforeSend: (event: Sentry.Event) => {
    const ignoreErrorCodes = ['ERR_NETWORK']
    const errorCode = event?.extra?.['Error code'] as string
    if (!!errorCode && ignoreErrorCodes?.includes(errorCode)) {
      return null
    }
    return event
  },
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'always',
      refetchOnWindowFocus: false,
      retry: 0, // Will retry failed requests 0 times before displaying an error
    },
  },
  queryCache: new QueryCache({
    onError: onQueryError,
    onSuccess: onQuerySuccess,
  }),
})

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(HttpApi)
  .init({
    supportedLngs: ['en', 'fi'],
    fallbackLng: 'en',
    detection: {
      order: ['cookie', 'htmlTag', 'localStorage', 'path'],
      caches: ['cookie'],
    },
    backend: {
      loadPath: `/${process.env.RELEASE_ID}/assets/locales/{{lng}}/translation.json`,
    },
    react: {
      useSuspense: false,
    },
    nsSeparator: false,
    keySeparator: '.',
  })

const SentryWithApp = Sentry.withProfiler(App)

// If the MSW_API_MOCK env is `enabled` start msw service worker.
// Used for mocking payloads in the application.
if (isMswEnabled) {
  const { mswWorker } = require('./msw/worker')
  mswWorker.start({
    onUnhandledRequest: 'bypass',
  })
}

const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <React.StrictMode>
    <Router>
      <FpjsProvider
        loadOptions={{
          endpoint: [FINGERPRINT_ENDPOINT],
          scriptUrlPattern: [FINGERPRINT_SCRIPT_URL_PATTERN],
          apiKey: process.env.FINGERPRINT_API_KEY,
          region: 'eu',
        }}
        cacheLocation={CacheLocation.LocalStorage}
        cacheTimeInSeconds={FINGERPRINT.CACHE_TIME}
      >
        <QueryClientProvider client={queryClient}>
          <LDProvider
            clientSideID={process.env.LAUNCH_DARKLY_ID}
            options={{
              sendEvents: true, // Dont send internal LD events back home to LD
              diagnosticOptOut: true, // Dont send diagnostics info
            }}
          >
            <SentryWithApp />
          </LDProvider>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </FpjsProvider>
    </Router>
  </React.StrictMode>
)
